import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Play, Loader2, User, Briefcase } from 'lucide-react';
import type { ClientPersona } from '@/data/insurance-scenarios';
import { InlineFeedbackTip, type FeedbackTip } from './InlineRealtimeFeedback';

interface ConversationPanelProps {
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
  isAISpeaking: boolean;
  persona: ClientPersona;
  isPracticeStarted: boolean;
  onStartPractice: () => void;
  isConnecting: boolean;
  language: 'en' | 'ko' | 'ja';
  feedbackTips?: FeedbackTip[]; // Optional feedback tips to display inline
}

export function ConversationPanel({
  transcript,
  isAISpeaking,
  persona,
  isPracticeStarted,
  onStartPractice,
  isConnecting,
  language,
  feedbackTips = [],
}: ConversationPanelProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';

  // Auto-scroll to bottom when new messages or feedback arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, feedbackTips]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header - Clean & Modern */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">
              {t('insurance.conversation')}
            </h2>
            <p className="text-xs text-gray-500 font-medium">
              {isPracticeStarted
                ? (isKorean ? '실시간 대화 기록' : isJapanese ? 'リアルタイム記録' : 'Live Transcript')
                : (isKorean ? '대화 대기 중' : isJapanese ? '開始待ち' : 'Waiting to start')
              }
            </p>
          </div>
        </div>

        {isAISpeaking && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" />
              <span
                className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '0.1s' }}
              />
              <span
                className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: '0.2s' }}
              />
            </div>
            <span className="text-blue-700 text-xs font-bold">{isKorean ? '고객 말하는 중' : isJapanese ? 'お客様が話しています' : 'Client speaking'}</span>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50">
        {!isPracticeStarted ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-sm w-full">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
              >
                {/* Client Avatar */}
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse opacity-50" />
                  <div className="relative w-full h-full rounded-full overflow-hidden ring-4 ring-white shadow-lg">
                    <img
                      src={persona.avatarUrl || "https://dopmo1eihgbgm.cloudfront.net/68cf9e94d79b5fba547884ff/korean-male.webp"}
                      alt="Client"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-green-500 border-4 border-white rounded-full z-10" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {isKorean ? `${persona.nameKo}님과의 상담` : isJapanese ? `${persona.nameKo}様との面談` : `Meeting with ${persona.name}`}
                </h3>

                <div className="flex items-center justify-center gap-2 mb-8">
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    {persona.age}{isJapanese ? '歳' : '세'}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    {(isKorean || isJapanese) ? persona.occupationKo : persona.occupation}
                  </span>
                </div>

                <button
                  onClick={onStartPractice}
                  disabled={isConnecting}
                  className="w-full group relative overflow-hidden px-6 py-4 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center justify-center gap-2">
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('insurance.connecting')}</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5 fill-current" />
                        <span>{t('insurance.startPractice')}</span>
                      </>
                    )}
                  </div>
                </button>

                <p className="mt-4 text-xs text-gray-400">
                  {isKorean
                    ? '마이크 권한이 필요합니다'
                    : isJapanese
                      ? 'マイクのアクセス許可が必要です'
                      : 'Microphone access required'}
                </p>
              </motion.div>
            </div>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {transcript.map((msg, index) => (
              <div key={index}>
                <MessageBubble
                  source={msg.source}
                  message={msg.message}
                  timestamp={msg.timestamp}
                  clientName={(isKorean || isJapanese) ? persona.nameKo : persona.name}
                  clientAvatarUrl={persona.avatarUrl}
                  language={language}
                />

                {/* Render feedback tips after user messages - Non-blocking */}
                {msg.source === 'user' && feedbackTips && feedbackTips.length > 0 && (() => {
                  try {
                    const messageTips = feedbackTips.filter((tip) => tip.messageIndex === index);
                    if (messageTips.length === 0) return null;

                    return (
                      <div className="relative mt-2 mb-4 pl-12">
                        <AnimatePresence mode="popLayout">
                          {messageTips.map((tip, tipIndex) => (
                            <InlineFeedbackTip
                              key={`tip-${index}-${tipIndex}`}
                              tip={tip}
                              // Add slight delay for multiple tips to create staggered effect
                              delay={tipIndex * 0.08}
                            />
                          ))}
                        </AnimatePresence>
                      </div>
                    );
                  } catch (error) {
                    // Silently catch any rendering errors - don't break the conversation UI
                    console.warn('⚠️ Error rendering feedback tip:', error);
                    return null;
                  }
                })()}
              </div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Footer */}
      {isPracticeStarted && (
        <div className="border-t border-gray-100 bg-white px-6 py-3">
          <div className="flex items-center justify-center gap-2 text-xs font-medium text-gray-500">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-600 rounded-md">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              <span>{isKorean ? '연결됨' : isJapanese ? '接続中' : 'Connected'}</span>
            </div>
            <span className="text-gray-300">|</span>
            <span>{isKorean ? '음성 대화가 진행 중입니다' : isJapanese ? '音声会話が進行中です' : 'Voice conversation is active'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface MessageBubbleProps {
  source: 'ai' | 'user';
  message: string;
  timestamp: Date;
  clientName: string;
  clientAvatarUrl?: string;
  language: 'en' | 'ko' | 'ja';
}

function MessageBubble({
  source,
  message,
  timestamp,
  clientName,
  clientAvatarUrl,
  language,
}: MessageBubbleProps) {
  const isClient = source === 'ai';
  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, type: "spring", stiffness: 500, damping: 30 }}
      className={`flex gap-4 ${isClient ? 'justify-start' : 'justify-end group'}`}
    >
      {isClient && (
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden shadow-sm mt-1">
          <img
            src={clientAvatarUrl || "https://dopmo1eihgbgm.cloudfront.net/68cf9e94d79b5fba547884ff/korean-male.webp"}
            alt="Client"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div
        className={`max-w-[80%] ${isClient ? 'items-start' : 'items-end'} flex flex-col gap-1.5`}
      >
        <div className="flex items-center gap-2 px-1">
          <span className="text-xs font-bold text-gray-700">
            {isClient ? clientName : (isKorean ? '나 (상담사)' : isJapanese ? 'あなた（アドバイザー）' : 'You (Agent)')}
          </span>
          <span className="text-[10px] font-medium text-gray-400">
            {timestamp.toLocaleTimeString(isKorean ? 'ko-KR' : isJapanese ? 'ja-JP' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>

        <div
          className={`relative rounded-2xl px-5 py-3.5 shadow-sm text-[15px] leading-relaxed ${
            isClient
              ? 'bg-white border border-gray-100 text-gray-800 rounded-tl-none'
              : 'bg-gray-900 text-white rounded-tr-none'
          }`}
        >
          <p>{message}</p>
        </div>
      </div>

      {!isClient && (
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md mt-1 ring-2 ring-white">
          <Briefcase className="w-5 h-5 text-white" />
        </div>
      )}
    </motion.div>
  );
}
